import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { translations, LanguageCode } from "../../utils/translations";

interface Blog {
  id: number;
  title: string;
  description: string;
  slug: string;
  category: string;
  date: string;
}

const blogs: Blog[] = [
  {
    id: 1,
    title: "How to Manage All Social Media in One Dashboard",
    description: "Learn how to control all platforms from one place.",
    slug: "manage-social-media-dashboard",
    category: "Social Media",
    date: "April 2026",
  },
  {
    id: 2,
    title: "Top Automation Tools for Businesses",
    description: "Best tools to save time and scale faster.",
    slug: "automation-tools-business",
    category: "Automation",
    date: "April 2026",
  },
  {
    id: 3,
    title: "How to Grow Your Business Online",
    description: "Proven strategies for digital growth.",
    slug: "grow-business-online",
    category: "Growth",
    date: "March 2026",
  },
];

interface BlogDashboardProps {
  lang?: LanguageCode;
}

const BlogDashboard: React.FC<BlogDashboardProps> = ({ lang = "en" }) => {
  const content = translations[lang].blogPage;
  const [search, setSearch] = useState("");
  const [activeCategory, setActiveCategory] = useState("All");

  // ✅ SEO TITLE
  useEffect(() => {
    document.title = content.seoTitle;
  }, [content.seoTitle]);

  // ✅ FILTER LOGIC
  const filteredBlogs = blogs.filter((blog) => {
    const matchSearch = blog.title.toLowerCase().includes(search.toLowerCase());

    const matchCategory =
      activeCategory === "All" || blog.category === activeCategory;

    return matchSearch && matchCategory;
  });

  return (
    <div style={{ padding: "40px 6%", background: "#fafafa" }}>
      {/* ================= HERO ================= */}
      <div style={{ maxWidth: "800px", marginBottom: "40px" }}>
        <h1 style={{ fontSize: "2.4rem", fontWeight: 800 }}>
          {content.title}
        </h1>

        <p style={{ color: "#555", marginTop: "10px", lineHeight: 1.6 }}>
          {content.intro}
        </p>
      </div>

      {/* ================= SEARCH ================= */}
      <div style={{ marginBottom: "30px" }}>
        <input
          type="text"
          placeholder={content.searchPlaceholder}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          style={{
            padding: "10px 14px",
            width: "100%",
            maxWidth: "400px",
            borderRadius: "8px",
            border: "1px solid #ddd",
          }}
        />
      </div>

      {/* ================= CATEGORIES ================= */}
      <div
        style={{
          display: "flex",
          gap: "10px",
          flexWrap: "wrap",
          marginBottom: "30px",
        }}
      >
        {content.categories.map((cat) => (
          <button
            key={cat.value}
            onClick={() => setActiveCategory(cat.value)}
            style={{
              padding: "6px 12px",
              borderRadius: "20px",
              border: "1px solid #ddd",
              background: activeCategory === cat.value ? "#111" : "#fff",
              color: activeCategory === cat.value ? "#fff" : "#333",
              cursor: "pointer",
              fontSize: "0.8rem",
            }}
          >
            {cat.label}
          </button>
        ))}
      </div>

      {/* ================= FEATURED ================= */}
      <div style={{ marginBottom: "40px" }}>
        <h2 style={{ fontSize: "1.4rem", marginBottom: "15px" }}>{content.featured}</h2>

        <Link
          to="/blogs/manage-social-media-dashboard"
          style={{ textDecoration: "none" }}
        >
          <div
            style={{
              padding: "20px",
              borderRadius: "12px",
              background: "#fff",
              boxShadow: "0 6px 20px rgba(0,0,0,0.05)",
            }}
          >
            <h3 style={{ fontSize: "1.2rem", marginBottom: "10px" }}>
              How to Manage All Social Media in One Dashboard
            </h3>
            <p style={{ color: "#666" }}>
              The easiest way to control all platforms from one place.
            </p>
          </div>
        </Link>
      </div>

      {/* ================= BLOG GRID ================= */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fill, minmax(260px, 1fr))",
          gap: "20px",
        }}
      >
        {filteredBlogs.map((blog) => (
          <Link
            key={blog.id}
            to={`/blogs/${blog.slug}`}
            style={{ textDecoration: "none" }}
          >
            <div
              style={{
                background: "#fff",
                borderRadius: "12px",
                padding: "18px",
                boxShadow: "0 6px 20px rgba(0,0,0,0.05)",
                height: "100%",
              }}
            >
              <div
                style={{
                  fontSize: "0.7rem",
                  color: "#0aa",
                  marginBottom: "6px",
                  fontWeight: 600,
                }}
              >
                {blog.category}
              </div>

              <h3 style={{ fontSize: "1rem", marginBottom: "8px" }}>
                {blog.title}
              </h3>

              <p style={{ fontSize: "0.85rem", color: "#666" }}>
                {blog.description}
              </p>

              <div
                style={{
                  marginTop: "10px",
                  fontSize: "0.75rem",
                  color: "#999",
                }}
              >
                {blog.date}
              </div>
            </div>
          </Link>
        ))}
      </div>
    </div>
  );
};

export default BlogDashboard;
